import {
  getSelectAllCheckState,
  SelectAllCheckState,
} from '@/components/select-all-checkbox';
import { Selection } from '@nextui-org/react';
import { FilterOption, TableFilter } from './types';

export function getSelectAllTargetUids(filter: TableFilter): string[] {
  return (
    filter.selectAllOptionUids ?? getSelectableFilterOptionUids(filter.options)
  );
}

export function areAllSelectAllTargetsSelected(
  selected: Selection | undefined,
  filter: TableFilter
): boolean {
  return getFilterSelectAllCheckState(selected, filter) === 'all';
}

export function getFilterSelectAllCheckState(
  selected: Selection | undefined,
  filter: TableFilter
): SelectAllCheckState {
  const targets = getSelectAllTargetUids(filter);

  if (targets.length === 0) {
    return 'none';
  }

  if (selected === 'all') {
    return 'all';
  }

  if (selected == null) {
    return 'none';
  }

  const selectedSet = new Set(Array.from(selected, String));
  const selectedCount = targets.filter((uid) => selectedSet.has(uid)).length;

  return getSelectAllCheckState(selectedCount, targets.length);
}

export function getSelectableFilterOptionUids(
  options: FilterOption[]
): string[] {
  return options
    .filter(
      (option) =>
        option.variant !== 'select-all' && option.variant !== 'divider'
    )
    .map((option) => option.uid);
}

export function areAllFilterOptionsSelected(
  selected: Selection | undefined,
  options: FilterOption[]
): boolean {
  const selectable = getSelectableFilterOptionUids(options);

  if (selectable.length === 0) {
    return false;
  }

  if (selected === 'all') {
    return true;
  }

  if (selected == null) {
    return false;
  }

  const selectedSet = new Set(Array.from(selected, String));
  return selectable.every((uid) => selectedSet.has(uid));
}

export function getVisibleFilterSelectedKeys(
  selected: Selection | undefined,
  filter: TableFilter
): Selection {
  if (selected === 'all') {
    return new Set(getSelectAllTargetUids(filter));
  }

  return new Set(
    Array.from(selected ?? [], String).filter((uid) => {
      const option = filter.options.find((entry) => entry.uid === uid);
      return option?.variant !== 'select-all' && option?.variant !== 'divider';
    })
  );
}

export function isMetaFilterOption(
  key: string,
  filter: TableFilter
): boolean {
  const option = filter.options.find((entry) => entry.uid === key);
  return option?.variant === 'select-all' || option?.variant === 'divider';
}

export function toggleFilterSelectAll(
  selected: Selection | undefined,
  filter: TableFilter
): Selection {
  const hadAll = areAllSelectAllTargetsSelected(selected, filter);
  return hadAll
    ? new Set<string>()
    : new Set(getSelectAllTargetUids(filter));
}

export function normalizeFilterSelectionChange(
  keys: Selection,
  previousKeys: Selection | undefined,
  filter: TableFilter
): Selection {
  if (filter.normalizeSelectionChange) {
    return filter.normalizeSelectionChange(keys, previousKeys);
  }

  if (keys === 'all') {
    return new Set(getSelectAllTargetUids(filter));
  }

  const keySet = new Set(Array.from(keys as Exclude<Selection, 'all'>, String));
  const selectAllOption = filter.options.find(
    (option) => option.variant === 'select-all'
  );

  if (selectAllOption && keySet.has(selectAllOption.uid)) {
    const hadAll = areAllSelectAllTargetsSelected(previousKeys, filter);
    return hadAll
      ? new Set<string>()
      : new Set(getSelectAllTargetUids(filter));
  }

  return new Set(
    Array.from(keySet).filter((key) => !isMetaFilterOption(key, filter))
  );
}
